import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    MenuItem,
    Grid,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    CircularProgress,
    Switch,
    FormControlLabel,
    Autocomplete
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Save as SaveIcon,
    Add as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { invoicesAPI, customersAPI } from '../api/api';
import { useSettings } from '../contexts/SettingsContext';
import { formatCurrency, formatDateForInput } from '../config';

const InvoiceForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const { gstEnabled, cgstRate, sgstRate } = useSettings();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [applyGst, setApplyGst] = useState(gstEnabled);

    const [formData, setFormData] = useState({
        invoice_date: new Date().toISOString().split('T')[0],
        customer_id: '',
        due_date: '',
        notes: ''
    });

    const [items, setItems] = useState([
        { description: '', hsn_code: '', quantity: 1, unit: 'KG', unit_price: 0 }
    ]);

    useEffect(() => {
        loadInitialData();
    }, [id]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            const custRes = await customersAPI.getActive();
            if (custRes.data.success) {
                setCustomers(custRes.data.data);
            }

            if (id) {
                const invRes = await invoicesAPI.getById(id);
                if (invRes.data.success) {
                    const inv = invRes.data.data;
                    setFormData({
                        invoice_date: formatDateForInput(inv.invoice_date),
                        customer_id: inv.customer_id,
                        due_date: formatDateForInput(inv.due_date) || '',
                        notes: inv.notes || ''
                    });
                    setSelectedCustomer(custRes.data.data.find(c => c.id === inv.customer_id));
                    setItems(inv.items.length > 0 ? inv.items : [{ description: '', hsn_code: '', quantity: 1, unit: 'KG', unit_price: 0 }]);
                    setApplyGst(inv.cgst_amount > 0 || inv.sgst_amount > 0);
                }
            }
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    const addItem = () => {
        setItems([...items, { description: '', hsn_code: '', quantity: 1, unit: 'KG', unit_price: 0 }]);
    };

    const removeItem = (index) => {
        if (items.length > 1) {
            setItems(items.filter((_, i) => i !== index));
        }
    };

    const calculateTotals = () => {
        const subtotal = items.reduce((sum, item) => {
            return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
        }, 0);

        if (applyGst) {
            const cgst = subtotal * (cgstRate / 100);
            const sgst = subtotal * (sgstRate / 100);
            return { subtotal, cgst, sgst, total: subtotal + cgst + sgst };
        }

        return { subtotal, cgst: 0, sgst: 0, total: subtotal };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedCustomer) {
            toast.error('Please select a customer');
            return;
        }

        if (items.some(item => !item.description || !item.unit_price)) {
            toast.error('Please fill in all item details');
            return;
        }

        try {
            setSaving(true);
            const payload = {
                ...formData,
                customer_id: selectedCustomer.id,
                items: items.map(item => ({
                    ...item,
                    quantity: parseFloat(item.quantity) || 1,
                    unit_price: parseFloat(item.unit_price) || 0
                })),
                apply_gst: applyGst
            };

            if (isEdit) {
                await invoicesAPI.update(id, payload);
                toast.success('Invoice updated successfully');
            } else {
                await invoicesAPI.create(payload);
                toast.success('Invoice created successfully');
            }

            navigate('/invoices');
        } catch (error) {
            console.error('Error saving invoice:', error);
            toast.error('Failed to save invoice');
        } finally {
            setSaving(false);
        }
    };

    const totals = calculateTotals();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button startIcon={<BackIcon />} onClick={() => navigate('/invoices')}>
                    Back
                </Button>
                <Typography variant="h5" fontWeight={600}>
                    {isEdit ? 'Edit Invoice' : 'New Invoice'}
                </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    {/* Invoice Details */}
                    <Grid item xs={12} md={8}>
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Invoice Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Autocomplete
                                            options={customers}
                                            getOptionLabel={(option) => option.name}
                                            value={selectedCustomer}
                                            onChange={(e, value) => setSelectedCustomer(value)}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Customer" required />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            label="Invoice Date"
                                            type="date"
                                            name="invoice_date"
                                            value={formData.invoice_date}
                                            onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                                            fullWidth
                                            required
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            label="Due Date"
                                            type="date"
                                            name="due_date"
                                            value={formData.due_date}
                                            onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Items */}
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6">Items</Typography>
                                    <Button startIcon={<AddIcon />} onClick={addItem} size="small">
                                        Add Item
                                    </Button>
                                </Box>

                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ minWidth: 200 }}>Description</TableCell>
                                                <TableCell sx={{ width: 100 }}>HSN</TableCell>
                                                <TableCell sx={{ width: 80 }}>Qty</TableCell>
                                                <TableCell sx={{ width: 80 }}>Unit</TableCell>
                                                <TableCell sx={{ width: 100 }}>Price</TableCell>
                                                <TableCell sx={{ width: 100 }} align="right">Amount</TableCell>
                                                <TableCell sx={{ width: 50 }}></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {items.map((item, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <TextField
                                                            value={item.description}
                                                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                            placeholder="Item description"
                                                            required
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            value={item.hsn_code}
                                                            onChange={(e) => handleItemChange(index, 'hsn_code', e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                            placeholder="HSN"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                            inputProps={{ min: 0, step: 0.001 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            select
                                                            value={item.unit}
                                                            onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                        >
                                                            <MenuItem value="KG">KG</MenuItem>
                                                            <MenuItem value="Bag">Bag</MenuItem>
                                                            <MenuItem value="Nos">Nos</MenuItem>
                                                            <MenuItem value="Ltr">Ltr</MenuItem>
                                                        </TextField>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            value={item.unit_price}
                                                            onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                            inputProps={{ min: 0, step: 0.01 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0))}
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => removeItem(index)}
                                                            disabled={items.length === 1}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>

                        {/* Notes */}
                        <Card>
                            <CardContent>
                                <TextField
                                    label="Notes"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    fullWidth
                                    multiline
                                    rows={2}
                                />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Summary */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ position: 'sticky', top: 16 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Summary</Typography>

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={applyGst}
                                            onChange={(e) => setApplyGst(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Apply GST"
                                    sx={{ mb: 2 }}
                                />

                                <Divider sx={{ mb: 2 }} />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography>Subtotal</Typography>
                                    <Typography fontWeight={500}>{formatCurrency(totals.subtotal)}</Typography>
                                </Box>

                                {applyGst && (
                                    <>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="body2" color="text.secondary">CGST ({cgstRate}%)</Typography>
                                            <Typography variant="body2">{formatCurrency(totals.cgst)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="body2" color="text.secondary">SGST ({sgstRate}%)</Typography>
                                            <Typography variant="body2">{formatCurrency(totals.sgst)}</Typography>
                                        </Box>
                                    </>
                                )}

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                    <Typography variant="h6">Total</Typography>
                                    <Typography variant="h6" color="primary" fontWeight={700}>
                                        {formatCurrency(totals.total)}
                                    </Typography>
                                </Box>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    disabled={saving}
                                >
                                    {isEdit ? 'Update Invoice' : 'Create Invoice'}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};

export default InvoiceForm;
